import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, PaymentMethod, StaffEntry, StaffInput, StaffList, StaffRole } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Dialog } from "../../ui/Dialog";
import {
  grantable,
  isException,
  MANAGER_ROLES,
  RIGHTS,
  rightValue,
  ROLE_ORDER,
  setRight,
  STAFF_PAYMENT_METHODS,
  type Exceptions,
} from "./rights";
import { phoneOnLeave, typedPhone } from "../../lib/phone";

interface Props {
  authorization: Authorization;
  roles: StaffList["roles"];
  /** null: a new staff member. */
  member: StaffEntry | null;
  onSaved: (member: StaffEntry) => void;
  /** Removed for good: someone who never did anything in Tafo. */
  onRemoved: (member: StaffEntry) => void;
  onClose: () => void;
}

interface Draft {
  name: string;
  role: StaffRole;
  pin: string;
  phone: string;
  active: boolean;
  exceptions: Exceptions;
  /** null = every method. */
  methods: PaymentMethod[] | null;
}

/**
 * "Nieuwe medewerker" / "Medewerker bewerken", as in the prototype. The code is never shown again
 * once saved (only a fingerprint is kept): leaving it empty keeps it, "Nieuwe code" proposes four
 * random digits (one in use is refused on saving: "Nieuwe code" again). A manager only picks Personeel,
 * Ober or Koerier and cannot change rights.
 */
export function StaffDialog({ authorization, roles, member, onSaved, onRemoved, onClose }: Props) {
  const { api, toast } = useTill();
  const owner = authorization.staff.role === "owner";
  const text = t.staffAdmin.dialog;
  const [draft, setDraft] = useState<Draft>(() => ({
    name: member?.name ?? "",
    role: member?.role ?? "staff",
    pin: "",
    phone: member?.phone ?? "",
    active: member?.active ?? true,
    exceptions: member?.permissions ?? {},
    methods: member ? member.paymentMethods : null,
  }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const set = (change: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };
  const roleOptions = ROLE_ORDER.filter((role) => owner || MANAGER_ROLES.includes(role));
  const defaults = roles[draft.role];

  const newCode = async () => {
    try {
      const { code } = await api.request<{ code: string }>("GET", "/device/staff/free-code", {
        staffToken: authorization.token,
      });
      set({ pin: code });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  };

  const save = async () => {
    const name = draft.name.trim();
    const pin = draft.pin.trim();
    const problem = !name
      ? text.noName
      : (member === null || pin !== "") && !/^\d{4}$/.test(pin)
        ? text.badCode
        : draft.methods?.length === 0
          ? text.noMethod
          : null;
    if (problem !== null) {
      setError(problem);
      return;
    }
    const body: StaffInput = {
      name,
      role: draft.role,
      phone: typedPhone(draft.phone) || null,
      active: draft.active,
      ...(pin !== "" && { pin }),
      // only the owner sends rights and payment methods; for a new courier the server starts with cash only
      ...(owner && { permissions: draft.role === "owner" ? {} : draft.exceptions }),
      ...(owner && { paymentMethods: draft.methods }),
      ...(!owner && member !== null && { permissions: member.role === draft.role ? member.permissions : {} }),
    };
    setBusy(true);
    setError("");
    try {
      const answer = await api.request<{ staff: StaffEntry }>(
        member ? "PUT" : "POST",
        member ? `/device/staff/${member.id}` : "/device/staff",
        { staffToken: authorization.token, body },
      );
      toast(t.staffAdmin.saved(answer.staff.name));
      onSaved(answer.staff);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  /** "Verwijderen": the server refuses anyone with history and says to put him uit dienst instead. */
  const remove = async () => {
    if (member === null) return;
    setRemoving(false);
    setBusy(true);
    setError("");
    try {
      await api.request("DELETE", `/device/staff/${member.id}`, { staffToken: authorization.token });
      onRemoved(member);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  const methodsOn = draft.methods ?? STAFF_PAYMENT_METHODS;
  const toggleMethod = (method: PaymentMethod) => {
    const next = methodsOn.includes(method) ? methodsOn.filter((item) => item !== method) : [...methodsOn, method];
    const all = STAFF_PAYMENT_METHODS.every((item) => next.includes(item));
    set({ methods: all ? null : STAFF_PAYMENT_METHODS.filter((item) => next.includes(item)) });
  };

  return (
    <>
      <Dialog
        title={member ? text.edit : text.new}
        size="wide"
        onClose={onClose}
        footer={
          <>
            {member !== null && member.id !== authorization.staff.id && (
              <button
                type="button"
                className="btn danger"
                disabled={busy}
                onClick={() => {
                  setRemoving(true);
                }}
              >
                {text.remove}
              </button>
            )}
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose}>
              {text.cancel}
            </button>
            <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
              {text.save}
            </button>
          </>
        }
      >
        <div className="sfrm">
          <label>
            {text.name}
            <input
              value={draft.name}
              autoComplete="off"
              maxLength={60}
              data-autofocus
              onChange={(event) => {
                set({ name: event.target.value });
              }}
            />
          </label>
          <label>
            {text.role}
            <select
              value={draft.role}
              onChange={(event) => {
                // another role starts from that role's rights (prototype)
                const role = event.target.value as StaffRole;
                // a new courier starts with cash only (besluiten 30/09)
                set({
                  role,
                  exceptions: {},
                  ...(member === null && { methods: role === "courier" ? (["cash"] as PaymentMethod[]) : null }),
                });
              }}
            >
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {t.roles[role]}
                </option>
              ))}
            </select>
          </label>
          <label>
            {text.code}
            <span className="inl">
              <input
                className="num"
                inputMode="numeric"
                maxLength={4}
                value={draft.pin}
                placeholder={member ? "••••" : ""}
                style={{ width: 90 }}
                onChange={(event) => {
                  set({ pin: event.target.value.replace(/\D/g, "") });
                }}
              />
              <button type="button" className="btn" onClick={() => void newCode()}>
                {text.newCode}
              </button>
            </span>
            {member && <small className="note">{text.codeKept}</small>}
          </label>
          <label>
            {text.phone}
            <input
              value={draft.phone}
              inputMode="tel"
              autoComplete="off"
              maxLength={30}
              onChange={(event) => {
                set({ phone: event.target.value });
              }}
              onBlur={() => {
                const written = phoneOnLeave(draft.phone);
                if (written !== null) set({ phone: written });
              }}
            />
          </label>
          <label className="inl">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(event) => {
                set({ active: event.target.checked });
              }}
            />{" "}
            {text.inService}
          </label>
        </div>

        {draft.role === "owner" ? (
          <p className="note">{text.ownerAll}</p>
        ) : (
          <>
            <h3 className="sfh">{text.rights}</h3>
            <div className="rightsl">
              {RIGHTS.map((right) => {
                const value = rightValue(defaults, draft.exceptions, right);
                const exception = isException(draft.exceptions, right);
                const fixed = !grantable(draft.role, right);
                return (
                  <label
                    key={right.id}
                    className={`inl${exception ? " ovr" : ""}`}
                    title={fixed ? text.fixedTitle : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={value}
                      disabled={!owner || fixed}
                      onChange={(event) => {
                        set({ exceptions: setRight(defaults, draft.exceptions, right, event.target.checked) });
                      }}
                    />{" "}
                    {right.name}
                    {exception && <span className="pill warn">{text.exception}</span>}
                  </label>
                );
              })}
            </div>
            {owner && Object.keys(draft.exceptions).length > 0 && (
              <button
                type="button"
                className="linkbtn"
                onClick={() => {
                  set({ exceptions: {} });
                }}
              >
                {text.reset(t.roles[draft.role])}
              </button>
            )}
            <h3 className="sfh">{text.methods}</h3>
            <div className="rightsl">
              {STAFF_PAYMENT_METHODS.map((method) => (
                <label key={method} className="inl">
                  <input
                    type="checkbox"
                    checked={methodsOn.includes(method)}
                    disabled={!owner}
                    onChange={() => {
                      toggleMethod(method);
                    }}
                  />{" "}
                  {t.paymentMethods[method]}
                </label>
              ))}
            </div>
            <p className="note">{text.methodsNote}</p>
          </>
        )}
        <p className="pinmsg" role="alert">
          {error}
        </p>
      </Dialog>
      {removing && member !== null && (
        <ConfirmDialog
          message={text.removeConfirm(member.name)}
          confirm={text.remove}
          onConfirm={() => void remove()}
          onClose={() => {
            setRemoving(false);
          }}
        />
      )}
    </>
  );
}
